import React, { useState } from 'react';
import config from '../config';
import { login } from '../contexts/loginContexts';
import { fetchAndCacheMenuItems } from '../services/menuCache';
import { storeAuthSession } from '../utils/authUser';

const BRAND = {
  primary: '#C04C2A',
  secondary: '#EC7347',
  accent: '#F7A35C',
  dark: '#4A1B13',
  soft: '#FFF4EC',
  border: '#D9B6A8',
};

const BRAND_GRADIENT = `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.secondary} 58%, ${BRAND.accent} 100%)`;

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const payload = {
      username: formData.email,
      password: formData.password,
    };

    try {
      const response = await login(`${config.baseurl}Auth/login`, payload);
      console.log('response', response);
      if (response?.status === 200) {
        const storedUser = storeAuthSession(response.data);
        try {
          await fetchAndCacheMenuItems(storedUser?.userId || response.data.user?.userId);
        } catch (e) {
          // Menu will be fetched by Sidebar hook if this fails.
        }
        window.location.href = '/dashboard';
      } else {
        setError(response?.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        backgroundColor: '#fffaf6',
        backgroundImage: `
          radial-gradient(circle at top left, rgba(247, 163, 92, 0.22), transparent 32%),
          radial-gradient(circle at bottom right, rgba(192, 76, 42, 0.18), transparent 30%),
          linear-gradient(145deg, #fffaf6 0%, #fff2e7 50%, #fde6d9 100%)
        `,
      }}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-3xl border bg-white/95 shadow-[0_25px_70px_rgba(74,27,19,0.16)] backdrop-blur-sm"
        style={{ borderColor: '#f1d3c6' }}
      >
        <div className="h-2 w-full" style={{ background: BRAND_GRADIENT }} />

        <div className="px-6 pt-6">
          <div
            className="inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em]"
            style={{ borderColor: '#f4d7c8', color: '#8B2A15' }}
          >
            BanglaFire CRM
          </div>
        </div>

        <div className="flex justify-center px-6 pt-5">
          <div
            className="flex w-full max-w-[260px] items-center justify-center rounded-2xl border px-5 py-4 shadow-sm"
            style={{
              borderColor: '#f5ddd1',
              background: `linear-gradient(180deg, ${BRAND.soft} 0%, #ffffff 100%)`,
            }}
          >
            <img
              src="/maillogo.png"
              alt="Company Logo"
              className="h-16 w-auto"
            />
          </div>
        </div>

        <div className="space-y-6 p-6">
          <h1
            className="text-center text-2xl font-semibold tracking-tight"
            style={{ color: BRAND.dark }}
          >
            Sign in to your account
          </h1>


          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="name@company.com"
                className="w-full rounded-xl border bg-white p-3 text-sm text-gray-900 outline-none transition focus:border-[#C04C2A] focus:ring-4"
                style={{
                  borderColor: BRAND.border,
                  '--tw-ring-color': 'rgba(236, 115, 71, 0.22)',
                }}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="********"
                className="w-full rounded-xl border bg-white p-3 text-sm text-gray-900 outline-none transition focus:border-[#C04C2A] focus:ring-4"
                style={{
                  borderColor: BRAND.border,
                  '--tw-ring-color': 'rgba(236, 115, 71, 0.22)',
                }}
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <a
                href="/forgot-password"
                className="font-medium transition hover:underline"
                style={{ color: BRAND.primary }}
              >
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-105 focus:ring-4 disabled:opacity-60"
              style={{
                background: BRAND_GRADIENT,
                '--tw-ring-color': 'rgba(236, 115, 71, 0.28)',
                boxShadow: '0 16px 30px rgba(192, 76, 42, 0.22)',
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Login;
